<script setup>
import {computed, onMounted, onUnmounted, reactive, ref} from 'vue';
import { getBrowserTimeZone } from '@/utils/browserTimeZone.js'
import { useNotificationStore } from '@/stores/NotificationStore.js'

const notificationStore = useNotificationStore()
const props = defineProps({
        analysisUuid: {
            type: String,
            required: false,
            default: ''
        },
        redirectRoute: {
            type: String,
            required: false,
            default: 'expert.analyze.attach'
        },
        inviterUuid: {
            type: String,
            required: false,
            default: null
        },
        presetUserName: {
            type: String,
            required: false,
            default: '',
        },
        presetUserEmail: {
            type: String,
            required: false,
            default: '',
        },
        submitLabel: {
            type: String,
            required: false,
            default: 'Register for free'
        },
        webinarId: {
            type: [String, Number],
            required: false,
            default: null,
        }
    })

    // Answers collected by the /discover chat — when present the page acts as a
    // verification step: everything is prefilled and only password + terms are new.
    const discoverContact = (() => {
        try {
            const raw = window.sessionStorage?.getItem('myflow-discover-contact');
            return raw ? JSON.parse(raw) : null;
        } catch (e) {
            return null;
        }
    })();
    const verifyMode = !!discoverContact;

    const registration = reactive({
        user: {
            name: props.presetUserName || discoverContact?.name || '',
            email: props.presetUserEmail || discoverContact?.email || '',
            password: '',
        },
        company: {
            name: discoverContact?.company_name || '',
            org_nr: '',
            country: discoverContact?.country_code || '',
        },
        term_agreements: {
            data_processing: false,
            business_terms: false,
            user_agreement: false,
        },
    });

    // The AI teammates picked in the discover chat — shown under the CTA as
    // a reminder of what starts working the moment they register.
    const teamBenefits = {
        website: { teammate: 'Web Designer', benefit: 'Builds your website — live in minutes', image: 'https://myflow.fra1.digitaloceanspaces.com/ai-agent/character/web-designer-v1.webp', bubbleClass: 'bg-aiAgentWebDesigner/20 border-aiAgentWebDesigner/40' },
        leads: { teammate: 'Lead Finder', benefit: 'Finds new clients that fit your business', image: 'https://myflow.fra1.digitaloceanspaces.com/ai-agent/character/lead-finder-v1.webp', bubbleClass: 'bg-aiAgentLeadFinder/20 border-aiAgentLeadFinder/40' },
        newsletter: { teammate: 'Newsletter Specialist', benefit: 'Drafts and sends your newsletters', image: 'https://myflow.fra1.digitaloceanspaces.com/ai-agent/character/newsletter-specialist-v1.webp', bubbleClass: 'bg-aiAgentNewsletterSpecialist/20 border-aiAgentNewsletterSpecialist/40' },
        content: { teammate: 'Content Creator', benefit: 'Plans and creates a week of content at a time', image: 'https://myflow.fra1.digitaloceanspaces.com/ai-agent/character/content-creator-v1.webp', bubbleClass: 'bg-aiAgentContentCreator/20 border-aiAgentContentCreator/40' },
        automations: { teammate: 'Automation Expert', benefit: 'Sets up follow-ups that send themselves', image: 'https://myflow.fra1.digitaloceanspaces.com/ai-agent/character/automation-expert-v1.webp', bubbleClass: 'bg-aiAgentAutomationExpert/20 border-aiAgentAutomationExpert/40' },
        sales: { teammate: 'Sales Assistant', benefit: 'Chases quotes and e-signing for you', image: 'https://myflow.fra1.digitaloceanspaces.com/ai-agent/character/sales-assistant-v1.webp', bubbleClass: 'bg-aiAgentSalesAssistant/20 border-aiAgentSalesAssistant/40' },
    };
    const selectedTeam = (discoverContact?.handoff ?? []).map(key => teamBenefits[key]).filter(Boolean);

    // Verify mode: prefilled details collapse into a summary; the inputs stay
    // mounted (v-show) so their validation keeps driving the submit button.
    const editingDetails = ref(false)
    const countryLabel = ref(discoverContact?.country || '')

    const nameValidates = ref(false)
    const emailValidates = ref(false)
    const existingUser = ref(false)
    const passwordValidates = ref(false)
    const displayPassword = ref(false)

    const companyNameValidates = ref(false)
    const companyCountryValidates = computed(() => registration.company.country !== '')


    const step = ref('user');
    const working = ref(false)
    const emailValidationTimeout = ref(null)

    const summaryRows = computed(() => [
        { label: 'Name', value: registration.user.name, valid: nameValidates.value },
        { label: 'Email', value: registration.user.email, valid: emailValidates.value },
        { label: 'Business', value: registration.company.name, valid: companyNameValidates.value },
        { label: 'Country', value: countryLabel.value || registration.company.country, valid: companyCountryValidates.value },
    ]);

    const userValidates = computed(() => {
        return nameValidates.value && emailValidates.value && passwordValidates.value
    })

    const companyValidates = computed(() => {
        return companyNameValidates.value
            && companyCountryValidates.value
            && registration.term_agreements.data_processing
            && registration.term_agreements.business_terms
    })

    const goBackToUser = () => {
        step.value = 'user'
    }

    const continueToCompany = () => {
        if (!userValidates.value) {
            return
        }

        step.value = 'interlude'
        setTimeout(() => {
            step.value = ''
        }, 2900)
        setTimeout(() => {
            step.value = 'company'
        }, 3500)
    }

    const submitRegistration = async () => {
        if (working.value || !companyValidates.value || !userValidates.value) {
            return
        }
        working.value = true

        const registrationData = {
            name: registration.user.name,
            email: registration.user.email,
            password: registration.user.password,
            time_zone: getBrowserTimeZone(),
            company_name: registration.company.name,
            country: registration.company.country,
            redirectRoute: props.redirectRoute,
            redirectParams: {uuid: props.analysisUuid}
        }

        if (props.webinarId) {
            registrationData.webinar_id = props.webinarId
        }

        const res = await axios.post(route('expert.register.submit', props.inviterUuid), registrationData);

        if (res.data.success) {
            step.value = 'registered'

            try {
                window.sessionStorage?.removeItem('myflow-discover-contact')
            } catch (e) {
                // ignore
            }

            setTimeout(function() {
                window.location.href = res.data.redirectUrl
            }, 2000)
        }
        if (res.data.error) {
            notificationStore.addNotification({
                type: 'warning',
                title: 'Email already registered',
                message: `There already is an account associated with ${registration.user.email}`,
                dismissAfter: 5000,
            })
        }
        working.value = false
    }

    const validateEmailDebounced = () => {
        if (emailValidationTimeout.value) {
            clearTimeout(emailValidationTimeout.value)
        }

        emailValidationTimeout.value = setTimeout(async () => {
            if (registration.user.email && emailValidates.value) {
                const res = await axios.post(route('expert.register.emailValidate'), {email: registration.user.email});
                existingUser.value = res.data.exists;
            }
        }, 500)
    }

    onMounted(() => {
        if (registration.user.email) {
            validateEmailDebounced()
        }

        window.addEventListener('keyup', (event) => {
            if (event.key === 'Enter') {
                if (step.value === 'user') {
                    continueToCompany()
                }
                if (step.value === 'company') {
                    submitRegistration()
                }
            }
        })
    })

    onUnmounted(() => {
        if (emailValidationTimeout.value) {
            clearTimeout(emailValidationTimeout.value)
        }
    })

</script>

<template>
    <div class="relative max-w-xl mx-auto">
        <div
            class="relative z-10 text-gray-900 transition-all"
            :class="step === 'user' ? '' : '-translate-x-vw'"
        >
            <div v-if="verifyMode" class="flex gap-3 mb-6">
                <div class="flex-shrink-0 w-10 h-10 rounded-full bg-wlPrimary/10 flex items-center justify-center">
                    <ai-agent-ai-orchestrator-avatar class="w-8 h-8"></ai-agent-ai-orchestrator-avatar>
                </div>
                <div class="px-4 py-3 bg-gray-50 text-gray-700" style="border-radius: 18px 18px 18px 8px;">
                    Almost there! I've filled in your answers from our chat — double-check them, pick a password, and your AI team gets to work.
                </div>
            </div>

            <card-basic>

                <!-- Verify mode: chat answers shown as a confirmation summary -->
                <div v-if="verifyMode && !editingDetails && !existingUser" class="mb-6">
                    <div class="rounded-2xl border border-myflowPurple/15 bg-gradient-to-br from-myflowPurple/5 via-white to-white shadow-sm divide-y divide-myflowPurple/10 overflow-hidden">
                        <div
                            v-for="row in summaryRows"
                            :key="row.label"
                            class="flex items-center gap-4 px-5 py-3.5"
                        >
                            <span class="w-20 flex-shrink-0 text-[11px] font-semibold uppercase tracking-widest text-myflowPurple/60">{{ row.label }}</span>
                            <span class="flex-1 min-w-0 truncate text-sm font-medium text-gray-900">{{ row.value }}</span>
                            <i
                                v-if="row.valid"
                                class="fa-solid fa-circle-check text-myflowGreen-600 flex-shrink-0"
                            ></i>
                        </div>
                    </div>
                    <div class="mt-2.5 flex justify-end">
                        <button
                            type="button"
                            class="text-xs text-gray-500 underline decoration-gray-300 hover:text-myflowPurple hover:decoration-myflowPurple"
                            @click="editingDetails = true"
                        >
                            Edit my details
                        </button>
                    </div>
                </div>

                <div v-show="!verifyMode || editingDetails || existingUser">
                <div class="relative space-x-2">
                    <input-text
                        class="w-full"
                        type="text"
                        label="Your name"
                        maxlength="255"
                        name="name"
                        @keypress="$event.key.match(/[a-zA-Z\s]/) ? true : $event.preventDefault()"
                        @validatesAs="$event === 'required' ? nameValidates = true : null"
                        @clearValidates="nameValidates = false"
                        required
                        v-model:content="registration.user.name"
                    />
                    <div class="absolute top-8 right-8">
                        <validation-mark
                            class="text-myflowGreen-600"
                            markClass="w-6 h-6"
                            v-if="nameValidates"
                        />
                    </div>
                </div>
                <div class="relative space-x-2">
                    <input-text
                        label="Email"
                        maxlength="255"
                        inputtype="email"
                        name="email"
                        @validatesAs="$event === 'email' ? emailValidates = true : null"
                        @clearValidates="emailValidates = false"
                        required
                        v-model:content="registration.user.email"
                        @keyup="validateEmailDebounced"
                    />
                    <div class="absolute top-8 right-8">
                        <validation-mark
                            class="text-myflowGreen-600"
                            markClass="w-6 h-6"
                            v-if="emailValidates"
                        />
                    </div>
                </div>
                </div>

                <div v-if="existingUser">

                    <p>
                        Welcome back! It looks like you already have an account with us.
                    </p>

                    <a :href="route('login')">
                        <button-base
                            style-type="success"
                        >
                            To login, click here
                        </button-base>
                    </a>
                </div>

                <div v-if="!existingUser">
                    <div v-show="!verifyMode || editingDetails">
                    <div class="relative mt-3 space-x-2">
                        <input-text
                            class="w-full"
                            type="text"
                            label="Business name"
                            maxlength="255"
                            name="name"
                            @validatesAs="$event === 'required' ? companyNameValidates = true : null"
                            @clearValidates="companyNameValidates = false"
                            required
                            v-model:content="registration.company.name"
                        />
                        <div class="absolute top-8 right-8">
                            <validation-mark
                                class="text-myflowGreen-600"
                                markClass="w-6 h-6"
                                v-if="companyNameValidates"
                            />
                        </div>
                    </div>
                    <div class="relative space-x-2">
                        <div>
                            <input-language
                                label="Country"
                                :required="true"
                                name="country"
                                :country="discoverContact?.country ?? ''"
                                v-model:content="registration.company.country"
                                @selected="countryLabel = $event.label"
                            ></input-language>
                        </div>
                        <div class="absolute top-12 right-8">
                            <validation-mark
                                class="text-myflowGreen-600"
                                markClass="w-6 h-6"
                                v-if="companyCountryValidates"
                            />
                        </div>
                    </div>

                    <button
                        v-if="verifyMode && editingDetails"
                        type="button"
                        class="mb-4 text-xs text-gray-500 underline decoration-gray-300 hover:decoration-myflowPurple"
                        @click="editingDetails = false"
                    >
                        Done editing
                    </button>
                    </div>

                    <div class="relative space-x-2">
                        <div>
                            <div class="relative">
                                <input-text
                                    label="Password"
                                    :inputtype="displayPassword ? 'text' : 'password'"
                                    maxlength="255"
                                    name="password"
                                    @validatesAs="$event === 'password' ? passwordValidates = true : null"
                                    @clearValidates="passwordValidates = false"
                                    required
                                    v-model:content="registration.user.password"
                                    class="!mb-0"
                                />
                                <button
                                    class="absolute bottom-0 top-6 right-0 w-12"
                                    @click.prevent="displayPassword = !displayPassword"
                                >
                                    <i
                                        class="fa-solid cursor-pointer text-gray-600"
                                        :class="displayPassword ? 'fa-eye' : 'fa-eye-slash'"
                                    ></i>
                                </button>
                            </div>
                            <p class="text-xs text-gray-500">Min 8 characters, one lowercase letter, one uppercase letter and one number</p>
                        </div>
                        <div class="absolute top-8 right-8">
                            <validation-mark
                                class="text-myflowGreen-600"
                                markClass="w-6 h-6"
                                v-if="passwordValidates"
                            />
                        </div>
                    </div>

                    <div class="col-span-1 mt-3 space-y-2">
                        <label class="flex items-start gap-3 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                class="peer sr-only"
                                v-model="registration.term_agreements.data_processing"
                                name="agrees_to_data_processing"
                                required
                                value="yes"
                            >
                            <span
                                class="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border transition-all duration-150 peer-focus-visible:ring-2 peer-focus-visible:ring-myflowPurple/40"
                                :class="registration.term_agreements.data_processing
                                    ? 'border-myflowPurple bg-myflowPurple shadow-sm'
                                    : 'border-gray-300 bg-white hover:border-myflowPurple/60'"
                            >
                                <i
                                    class="fa-solid fa-check text-[10px] text-white transition-all duration-150"
                                    :class="registration.term_agreements.data_processing ? 'opacity-100 scale-100' : 'opacity-0 scale-50'"
                                ></i>
                            </span>
                            <span class="text-sm text-gray-700">
                                I accept the
                                <a
                                    :href="route('terms.pub')"
                                    class="underline decoration-gray-300 hover:decoration-myflowPurple text-myflowPurple-900"
                                    target="_blank"
                                    @click.stop
                                >data processing agreement</a>
                            </span>
                        </label>
                        <label class="flex items-start gap-3 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                class="peer sr-only"
                                v-model="registration.term_agreements.business_terms"
                                name="agrees_to_business_terms"
                                required
                                value="yes"
                            >
                            <span
                                class="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border transition-all duration-150 peer-focus-visible:ring-2 peer-focus-visible:ring-myflowPurple/40"
                                :class="registration.term_agreements.business_terms
                                    ? 'border-myflowPurple bg-myflowPurple shadow-sm'
                                    : 'border-gray-300 bg-white hover:border-myflowPurple/60'"
                            >
                                <i
                                    class="fa-solid fa-check text-[10px] text-white transition-all duration-150"
                                    :class="registration.term_agreements.business_terms ? 'opacity-100 scale-100' : 'opacity-0 scale-50'"
                                ></i>
                            </span>
                            <span class="text-sm text-gray-700">
                                I accept the
                                <a
                                    :href="route('terms.company')"
                                    class="underline decoration-gray-300 hover:decoration-myflowPurple text-myflowPurple-900"
                                    target="_blank"
                                    @click.stop
                                >business terms</a> &
                                <a
                                    :href="route('terms.general')"
                                    class="underline decoration-gray-300 hover:decoration-myflowPurple text-myflowPurple-900"
                                    target="_blank"
                                    @click.stop
                                >user agreement</a>
                            </span>
                        </label>
                    </div>

                    <div class="flex justify-between mt-4">
                        <button
                            v-if="verifyMode"
                            type="button"
                            class="w-full px-8 py-3 text-white rounded-xl bg-myflowPurple text-lg shadow-lg hover:-translate-y-0.5 hover:shadow-xl transition-all duration-200 disabled:opacity-40 disabled:hover:translate-y-0"
                            :disabled="!companyValidates || !userValidates || working"
                            @click="submitRegistration"
                        >
                            {{ props.submitLabel }}
                        </button>
                        <button-base
                            v-else
                            :style-type="userValidates && companyValidates ? 'success' : 'disabled'"
                            :disabled="!companyValidates || !userValidates || working"
                            @click="submitRegistration"
                        >
                            {{ props.submitLabel }}
                        </button-base>
                    </div>

                    <div class="flex mt-4 space-x-4" :class="verifyMode ? 'justify-center' : 'justify-between'">
                        <p class="text-xs" :class="verifyMode ? 'text-gray-500' : ''">
                            No credit card required
                        </p>
                    </div>

                    <div v-if="verifyMode && selectedTeam.length" class="mt-6 border-t border-gray-100 pt-5">
                        <p class="text-center text-[11px] font-semibold uppercase tracking-widest text-myflowPurple/60">
                            Your AI team starts the moment you do
                        </p>
                        <div class="mt-4 space-y-2.5">
                            <div
                                v-for="member in selectedTeam"
                                :key="member.teammate"
                                class="flex items-center gap-3 rounded-xl border px-4 py-2.5"
                                :class="member.bubbleClass"
                            >
                                <img
                                    :src="member.image"
                                    :alt="member.teammate"
                                    class="w-10 h-10 flex-shrink-0 object-contain"
                                    loading="lazy"
                                />
                                <div class="min-w-0">
                                    <p class="text-sm font-semibold text-gray-800">{{ member.teammate }}</p>
                                    <p class="text-xs text-gray-600">{{ member.benefit }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
            </div>

            </card-basic>
        </div>

        <div
            class="absolute inset-0 flex items-center justify-center p-8 transition-all duration-1000 delay-300"
            :class="step === 'interlude' ? 'opacity-100 scale-100' : 'opacity-0 scale-90'"
        >
            <div class="text-center">
                <p>Just a moment while we make sure everything is set up perfectly</p>
                <p class="italic strong">You look great by the way! ☀️</p>
            </div>
        </div>

        <div
            class="relative z-10 text-gray-900 transition-all"
            :class="step === 'company' ? '' : 'opacity-0 disabled-translate-x-vw'"
        >

        </div>

        <div
            class="absolute inset-0 z-0 flex items-center justify-center p-8 transition-all duration-1000 delay-300 pointer-events-none"
            :class="step === 'registered' ? 'opacity-100 scale-100' : 'opacity-0 scale-90'"
        >
            <div class="text-center">
                <h1>Sweet</h1>
                <p>Your account is set up and we're putting the final pieces in place</p>
            </div>
        </div>
    </div>
</template>

<style scoped>
.translate-x-vw {
    transform: translateX(100vw);
}
.-translate-x-vw {
    transform: translateX(-100vw);
}
</style>
